// src/components/ui/Popover.tsx — SCE Issues
// Petit panneau flottant ancré sous un bouton (filtre d'une colonne, menu des
// colonnes, choix du regroupement).
//
// Rendu dans <body> : le tableau défile et coupe ce qui dépasse, un panneau
// resté dans ses cellules serait rogné. Se referme sur Échap, sur un clic à
// côté, et dès que la page ou le tableau défile (l'ancre aurait bougé).

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

export interface PopoverProps {
  /** Le bouton sous lequel le panneau s'ouvre. */
  ancre: HTMLElement
  onClose: () => void
  titre: string
  children: ReactNode
  largeur?: number
  /** Au-dessus d'une grande fenêtre (ouverture FNC-T : 81) ; défaut : la classe (62). */
  zIndex?: number
  /** Aligne le bord droit du panneau sur celui de l'ancre. */
  alignerADroite?: boolean
}

export function Popover({
  ancre,
  onClose,
  titre,
  children,
  largeur = 272,
  alignerADroite = false,
  zIndex,
}: PopoverProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const r = ancre.getBoundingClientRect()
    const w = el.offsetWidth
    const h = el.offsetHeight
    let left = alignerADroite ? r.right - w : r.left
    left = Math.max(8, Math.min(left, window.innerWidth - w - 8))
    let top = r.bottom + 6
    if (top + h > window.innerHeight - 8) top = Math.max(8, window.innerHeight - h - 8)
    setPosition({ top, left })
  }, [ancre, alignerADroite, children])

  useEffect(() => {
    function surTouche(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    function surClic(e: PointerEvent): void {
      const cible = e.target as Node
      // Un clic sur l'ancre est géré par l'ancre (elle bascule le panneau).
      if (ref.current?.contains(cible) || ancre.contains(cible)) return
      onClose()
    }
    function surDefilement(e: Event): void {
      if (ref.current?.contains(e.target as Node)) return
      onClose()
    }
    window.addEventListener('keydown', surTouche, true)
    window.addEventListener('pointerdown', surClic, true)
    window.addEventListener('scroll', surDefilement, true)
    window.addEventListener('resize', onClose)
    return () => {
      window.removeEventListener('keydown', surTouche, true)
      window.removeEventListener('pointerdown', surClic, true)
      window.removeEventListener('scroll', surDefilement, true)
      window.removeEventListener('resize', onClose)
    }
  }, [ancre, onClose])

  return createPortal(
    <div
      ref={ref}
      className="popover-flottant"
      role="dialog"
      aria-label={titre}
      style={{ width: largeur, top: position?.top ?? -9999, left: position?.left ?? -9999, ...(zIndex ? { zIndex } : {}) }}
    >
      {children}
    </div>,
    document.body,
  )
}

/**
 * Un panneau flottant est-il ouvert ? Les fenêtres qui ferment sur Échap
 * (ouverture FNC-T, relevé, modifier / clore) laissent alors la touche au
 * panneau : Échap ferme le calendrier, pas la fenêtre dessous.
 */
export function popoverOuvert(): boolean {
  return document.querySelector('.popover-flottant') !== null
}
